import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import { Button } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { useTranslation } from '@root/shared/localization'

import { TransferFeeEvm } from '../transfer-fee/transfer-fee-evm'
import { TransferFeeSvm } from '../transfer-fee/transfer-fee-svm'

import { EstimatedChanges } from './estimated-changes'
import { TransferReviewHeader } from './transfer-review-header'
import { TransferTotals } from './transfer-totals'

import type { TransferFlowReatom } from '../../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: TransferFlowReatom
} & ClassNameProps

export const TransferReviewContent: React.FC<Props> = ({
  className,
  model,
}) => {
  const { t } = useTranslation()

  const [state] = useAtom(model.transferStateAtom)
  const [network] = useAtom(selectedNetworkAtom)
  const submit = useAction(model.executeTransferAction)

  const renderFeeCard = () =>
    match(network)
      .with(Network.Solana, () => <TransferFeeSvm model={model.svmFeeModel} />)
      .with(P.union(Network.Ethereum, Network.Base, Network.Bsc), () => (
        <TransferFeeEvm model={model.gasSettings} />
      ))
      .with(Network.Ton, () => <></>)
      .exhaustive()

  return (
    <Root className={className}>
      <TransferReviewHeader model={model} />
      <Blocks>
        <EstimatedChanges model={model} />
        {renderFeeCard()}
      </Blocks>
      <Total>
        <TransferTotals model={model} />
      </Total>
      <ActionContainer>
        <Button
          radius="rounded"
          fullWidth
          busy={state.loading}
          onClick={submit}
          color="primary"
        >
          {t('common.confirm')}
        </Button>
      </ActionContainer>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const ActionContainer = styled.div`
  margin-top: 4px;
`
const Total = styled.div`
  padding-inline: 12px;
`

const Blocks = styled.div`
  display: grid;
  gap: 6px;

  padding-block: 8px;
  padding-inline: 6px;

  border-radius: 16px;
  border: 1px solid var(--background-2);
  background: var(--backgroundA-3);
`
